<template>
  <Card class="card">
    <View direction="column" gap="md">
      <View class="file-icon-wrap accepted" justify="center" align="center" v-if="bundleAccepted">
        <Icon class="file-icon" name="file-check-02" />
      </View>
      <View class="file-icon-wrap declined" justify="center" align="center" v-if="bundleDeclined">
        <Icon class="file-icon" name="file-x-02" />
      </View>

      <View direction="column" gap="sm">
        <Text variant="h2" :align="bundleSubmitted ? 'start' : 'center'">
          {{ bundleTexts.header }}
        </Text>
        <Text variant="body-medium" color="secondary" :align="bundleSubmitted ? 'start' : 'center'">
          {{ bundleTexts.body }}
        </Text>
      </View>

      <View direction="column" gap="md">
        <template v-if="bundleSubmitted">
          <Button @click="acceptApplicationModalRef?.modalRef?.show()" :appearance="'primary'">
            Accept application
          </Button>
          <Button @click="declineApplicationModalRef?.modalRef?.show()" :appearance="'danger'" :variant="'secondary'">
            Decline application
          </Button>
        </template>

        <template v-if="bundleDeclined || bundleAccepted">
          <Card class="contact-card" variant="outline" size="medium">
            <View direction="column" align="center">
              <Text variant="label-large" v-if="renter?.user.phoneNumber">
                <a :href="`tel:${renter?.user.phoneNumber}`">{{ renter?.user.phoneNumber }}</a>
              </Text>
              <Text variant="body-large" color="secondary" v-if="renter?.user.email">
                <a :href="`mailto:${renter?.user.email}`">{{ renter?.user.email }}</a>
              </Text>
            </View>
          </Card>
        </template>
      </View>
    </View>

    <AcceptRenterApplicationModal
      :bundle-id="bundleId"
      :renter-first-name="renterFirstName || ''"
      ref="acceptApplicationModalRef"
    />
    <DeclineRenterApplicationModal :bundle-id="bundleId" ref="declineApplicationModalRef" />
  </Card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { ApplicationBundleStatus } from '@tallo/applications';
import { Button, Card, Icon, Text, View } from '@tallo/design-system';

import { useRenterProfileStore } from '../store/renter-profile.store';
import AcceptRenterApplicationModal from './accept-renter-application-modal.vue';
import DeclineRenterApplicationModal from './decline-renter-application-modal.vue';

const acceptApplicationModalRef = ref<InstanceType<typeof AcceptRenterApplicationModal>>();
const declineApplicationModalRef = ref<InstanceType<typeof DeclineRenterApplicationModal>>();

const renterProfileStore = useRenterProfileStore();
const route = useRoute();

const bundleId = ref(route.params.applicationId as string);

const renter = computed(() => renterProfileStore.getRenterProfile);
const renterFirstName = computed(() => renter.value?.user.name.split(' ')[0]);

const bundle = computed(() => renterProfileStore.bundles.bundles.find((bundle) => bundle.id === bundleId.value));
const bundleTexts = computed(() => {
  switch (bundle.value?.status) {
    case ApplicationBundleStatus.ACCEPTED:
      return {
        header: 'Application accepted',
        body: 'Get your property rented faster by contacting your future renters',
      };
    case ApplicationBundleStatus.DECLINED:
      return {
        header: 'Application declined',
        body: 'You can still reach out to the renter if you changed your mind',
      };
    default:
      return {
        header: 'Meets your criteria?',
        body: 'Renters will be notified of your decision. Whether you accept or reject the application, Tallo will guide the renters through what comes next.',
      };
  }
});
const bundleSubmitted = computed(() => bundle.value?.status === ApplicationBundleStatus.SUBMITTED);
const bundleAccepted = computed(() => bundle.value?.status === ApplicationBundleStatus.ACCEPTED);
const bundleDeclined = computed(() => bundle.value?.status === ApplicationBundleStatus.DECLINED);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.file-icon-wrap {
  border-radius: var(--t-border-radius-full-rounded);
  align-self: center;
  width: 3rem;
  height: 3rem;

  &.declined {
    background-color: var(--t-color-red-100);

    .file-icon {
      color: var(--t-color-red-500);
    }
  }

  &.accepted {
    background-color: var(--t-color-green-200);

    .file-icon {
      color: var(--t-color-green-500);
    }
  }
}

.card {
  height: fit-content;
}

.contact-card.contact-card {
  background-color: var(--t-color-neutral-100);
}

@include screen-min-lg {
  .card {
    width: 100%;
    max-width: 23.75rem;
  }
}
</style>
